import {
  addDoc,
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore/lite";
import { useFormik } from "formik";
import React, {
  type FC,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import { useTranslation } from "react-i18next";
import { db } from "senf-shared";
import * as Yup from "yup";
import Box from "../../../atoms/box/Box";
import Input from "../../../atoms/inputs/Input";
import SelectGroup from "../../../atoms/inputs/SelectGroup";
import Typography from "../../../atoms/typography/Typography";
import Button from "../../../core/button/Button";
import Icon from "../../../core/icon/Icon";
import Spinner from "../../../core/spinner/Spinner";
import { useModals } from "../../../molecules/modalStack/ModalProvider";
import type {
  ReportCategory,
  ReportFormValues,
  ReportModalContentProps,
} from "./Report.types";

/**
 * Report component for reporting ideas or comments
 */
const Report: FC<ReportModalContentProps> = ({ data, project }) => {
  const { ideaId, commentId, ideaMapId } = data || {};
  const { projectId, organizationId } = project;
  const [submitted, setSubmitted] = useState(false);
  const [reported, setReported] = useState(false);
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);
  const [type, setType] = useState<"post" | "comment">(
    commentId ? "comment" : "post",
  );
  const { t } = useTranslation();
  const { closeModal } = useModals();
  const initialFocusRef = useRef<HTMLButtonElement>(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  const reportCategories: {
    value: ReportCategory;
    label: string;
    description: string;
  }[] = [
    {
      value: "spam",
      label: t("report:spam"),
      description: t("report:spamDescription", "Unwanted or repeated content"),
    },
    {
      value: "scamFraud",
      label: t("report:scamFraud"),
      description: t(
        "report:scamFraudDescription",
        "Deceptive or fraudulent content",
      ),
    },
    {
      value: "hateSpeech",
      label: t("report:hateSpeech"),
      description: t(
        "report:hateSpeechDescription",
        "Attacks people based on identity",
      ),
    },
    {
      value: "bullyingHarassment",
      label: t("report:bullyingHarassment"),
      description: t(
        "report:bullyingHarassmentDescription",
        "Threatening or intimidating behavior",
      ),
    },
    {
      value: "offensive",
      label: t("report:offensiveStatement"),
      description: t(
        "report:offensiveDescription",
        "Content that may be offensive",
      ),
    },
    {
      value: "falseInformation",
      label: t("report:falseInformation"),
      description: t(
        "report:falseInformationDescription",
        "Misleading or inaccurate information",
      ),
    },
    {
      value: "inappropriateContent",
      label: t("report:inappropriateContent", "Inappropriate Content"),
      description: t(
        "report:inappropriateContentDescription",
        "Content not suitable for all audiences",
      ),
    },
    {
      value: "privacyViolation",
      label: t("report:privacyViolation", "Privacy Violation"),
      description: t(
        "report:privacyViolationDescription",
        "Shares personal information without consent",
      ),
    },
    {
      value: "other",
      label: t("global:other"),
      description: t("report:otherDescription", "Other reasons not listed"),
    },
  ];

  // Validation schema for the form
  const validationSchema = Yup.object({
    reportCategory: Yup.string().required(t("validation:required", "Required")),
    reason: Yup.string().nullable(),
  });

  const formik = useFormik<ReportFormValues>({
    initialValues: {
      reportCategory: "",
      reason: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      if (!projectId) throw new Error("projectId not found in Report.tsx");
      if (!organizationId)
        throw new Error("organizationId not found in Report.tsx");
      if (!ideaId) throw new Error("ideaId not found in Report.tsx");

      // Check for duplicate reports before submission
      setIsCheckingDuplicate(true);
      const isDuplicate = await checkIfAlreadyReported();
      setIsCheckingDuplicate(false);

      if (isDuplicate) {
        setReported(true);
        return;
      }

      try {
        // add report to db
        await addDoc(collection(db, "reports"), {
          reportCategory: values.reportCategory,
          reason: values.reason,
          organizationId,
          projectId,
          ideaId,
          ideaMapId: ideaMapId || null,
          commentId: commentId || null,
          createdAt: new Date().toISOString(),
        });
      } catch (error) {
        throw new Error(`Error reporting idea or comment${error}`);
      } finally {
        setSubmitted((prev) => !prev);
        formik?.resetForm();
      }
    },
  });

  const handleRadioButtons = (newVal: string | string[]) => {
    // Handle both string and string array inputs
    const value = Array.isArray(newVal) ? newVal[0] : newVal;
    formik.setFieldValue("reportCategory", value);
  };

  // Duplicate check function to be used both on load and before submission
  const checkIfAlreadyReported = useCallback(async (): Promise<boolean> => {
    if (!ideaId) return false;
    try {
      const q = query(
        collection(db, "reports"),
        where("ideaId", "==", ideaId),
        where("commentId", "==", commentId || null),
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.size > 0;
    } catch (error) {
      console.error("Error checking if already reported:", error);
      return false; // Assume not reported in case of error
    }
  }, [ideaId, commentId]);

  useEffect(() => {
    let isMounted = true;
    const getReported = async () => {
      setIsInitialLoading(true);
      try {
        const isReported = await checkIfAlreadyReported();
        if (isMounted && isReported) {
          setReported(true);
        }
      } catch (error) {
        console.error("Error checking report status:", error);
      } finally {
        if (isMounted) {
          setIsInitialLoading(false);
        }
      }
    };
    getReported();

    // Focus management
    if (!submitted && !reported && initialFocusRef.current) {
      initialFocusRef.current.focus();
    }

    return () => {
      isMounted = false;
    };
  }, [checkIfAlreadyReported, submitted, reported]);

  return isInitialLoading ? (
    <Box
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      padding="20px 22px 26px"
      height="400px"
    >
      <Icon name="loading" />
    </Box>
  ) : submitted || reported ? (
    <div aria-live="polite" role="status">
      <Box
        flexDirection="column"
        alignItems="center"
        justifyContent="flex-end"
        padding="20px 22px 26px"
      >
        <Box flexDirection="column" marginTop="200px">
          <Icon
            name="checkmark-lg"
            className="text-icon-highlight w-16 h-16"
            aria-hidden="true"
          />
        </Box>
        <Box
          flexDirection="column"
          marginTop="20px"
          marginBottom="170px"
          padding="0 10px"
          gap="16px"
          textAlign="center"
        >
          {submitted ? (
            <>
              <Typography variant="h3">
                {String(t("global:messageSubmitted"))}
              </Typography>
              <Typography variant="bodyBg" fontWeight={400}>
                {String(t("report:reportSubmitted"))}
              </Typography>
            </>
          ) : (
            // already reported
            <>
              <Typography variant="h3">
                {String(
                  t("report:alreadyReported", {
                    type: t(`global:${type}`),
                  }),
                )}
              </Typography>
              <Typography variant="bodyBg" fontWeight={400} textAlign="center">
                {String(
                  t("report:alreadyReportedAndExplained", {
                    type: t(`global:${type}`),
                  }),
                )}
              </Typography>
            </>
          )}
        </Box>

        <Button
          variant="primary"
          width="max"
          onClick={() => {
            closeModal();
          }}
          ref={initialFocusRef}
        >
          {t("global:close")}
        </Button>
      </Box>
    </div>
  ) : (
    <div
      role="dialog"
      aria-labelledby="report-dialog-title"
      aria-describedby="report-dialog-description"
    >
      <Box flexDirection="column" padding="22px 22px 26px" height="100%">
        <Box flexDirection="column" marginBottom="6px">
          <Typography variant="h3" id="report-dialog-title">
            {String(t("util:report", { val: t(`global:${type}`) }))}
          </Typography>
        </Box>

        <Box flexDirection="column" marginBottom="16px">
          <Typography
            variant="bodySm"
            fontWeight={500}
            id="report-dialog-description"
          >
            {String(t("report:reportReason"))}
          </Typography>
        </Box>

        <form onSubmit={formik.handleSubmit}>
          <div role="radiogroup" aria-labelledby="report-dialog-description">
            <Box>
              <SelectGroup
                options={reportCategories}
                onChange={handleRadioButtons}
                value={formik.values.reportCategory}
                type="radio"
                aria-required="true"
                key={formik.values.reportCategory}
              />
            </Box>
          </div>

          <Box flexDirection="column" marginTop="26px" marginBottom="16px">
            <Typography
              variant="bodySm"
              fontWeight={500}
              id="report-reason-label"
            >
              {String(
                t("report:reportReasonOptional", {
                  type: t(`global:${type}`),
                }),
              )}
            </Typography>
          </Box>
          <Input
            name="reason"
            type="textarea"
            rows={1}
            onChange={formik.handleChange}
            value={formik.values.reason || ""}
            placeholder={String(t("global:optional"))}
            aria-labelledby="report-reason-label"
          />

          <div className="mt-6 flex justify-end gap-3">
            <Button
              width="max"
              size="lg"
              onClick={() => closeModal()}
              type="button"
            >
              {t("global:cancel")}
            </Button>
            <Button
              variant="primary"
              size="lg"
              width="max"
              type="submit"
              disabled={
                formik.values.reportCategory === "" || isCheckingDuplicate
              }
              ref={initialFocusRef}
            >
              {isCheckingDuplicate ? t("global:checking") : t("global:send")}
            </Button>
          </div>
        </form>
      </Box>
    </div>
  );
};

export default Report;
